import React from 'react';
import { Box, Button, TextField, Typography } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { StatusChip } from './StatusChip';

type LogViewerRow = {
  key: string;
  timestamp: string;
  severity: string;
  source: string;
  message: string;
};

type LogViewerProps = {
  emptyLabel: string;
  sourceLabel: string;
  rows: LogViewerRow[];
  maxHeight?: number;
};

const messageCellStyle: React.CSSProperties = {
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontFamily: 'Menlo, Monaco, Consolas, monospace',
  fontSize: 12,
  lineHeight: 1.45,
};

function normalize(value: string): string {
  return String(value || '')
    .trim()
    .toUpperCase();
}

export const LogViewer = ({
  emptyLabel,
  sourceLabel,
  rows,
  maxHeight = 520,
}: LogViewerProps) => {
  const theme = useTheme();
  const [query, setQuery] = React.useState('');
  const [severity, setSeverity] = React.useState('ALL');

  const severityOptions = React.useMemo(() => {
    const set = new Set<string>();
    rows.forEach(row => set.add(normalize(row.severity) || '-'));
    return ['ALL', ...Array.from(set).sort()];
  }, [rows]);

  const filteredRows = React.useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter(row => {
      const rowSeverity = normalize(row.severity) || '-';
      if (severity !== 'ALL' && rowSeverity !== severity) return false;
      if (!q) return true;
      const source = String(row.source || '').toLowerCase();
      const message = String(row.message || '').toLowerCase();
      const timestamp = String(row.timestamp || '').toLowerCase();
      return source.includes(q) || message.includes(q) || timestamp.includes(q);
    });
  }, [rows, query, severity]);

  if (rows.length === 0) {
    return (
      <Typography variant="body2" color="textSecondary">
        {emptyLabel}
      </Typography>
    );
  }

  return (
    <Box>
      <Box
        mb={2}
        display="flex"
        flexWrap="wrap"
        gridGap={10}
        alignItems="center"
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center" gridGap={10} flexWrap="wrap">
          <TextField
            size="small"
            variant="outlined"
            value={query}
            onChange={event => setQuery(event.target.value)}
            placeholder="Filter logs..."
            inputProps={{ 'aria-label': 'Filter logs text' }}
          />
          <Box display="flex" alignItems="center" gridGap={6} flexWrap="wrap">
            {severityOptions.map(option => (
              <Button
                key={option}
                size="small"
                variant={severity === option ? 'contained' : 'outlined'}
                color={severity === option ? 'primary' : 'default'}
                onClick={() => setSeverity(option)}
                aria-label={`Filter severity ${option}`}
              >
                {option}
              </Button>
            ))}
          </Box>
        </Box>

        <Typography variant="caption" color="textSecondary">
          {filteredRows.length} / {rows.length} entries
        </Typography>
      </Box>

      <Box
        style={{
          borderRadius: 10,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        <Box style={{ overflowX: 'auto' }}>
          <Box
            style={{
              display: 'grid',
              gridTemplateColumns:
                'minmax(150px, 170px) minmax(90px, 110px) minmax(150px, 220px) minmax(420px, 1fr)',
              gap: 12,
              alignItems: 'center',
              padding: '10px 12px',
              fontSize: 12,
              fontWeight: 600,
              color: theme.palette.text.secondary,
              borderBottom: `1px solid ${theme.palette.divider}`,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              background: theme.palette.background.default,
            }}
          >
            <Box>Timestamp</Box>
            <Box>Severity</Box>
            <Box>{sourceLabel}</Box>
            <Box>Summary</Box>
          </Box>
        </Box>

        <Box style={{ maxHeight, overflowY: 'auto', overflowX: 'auto' }}>
          {filteredRows.length === 0 ? (
            <Box p={2}>
              <Typography variant="body2" color="textSecondary">
                No logs match current filters.
              </Typography>
            </Box>
          ) : (
            filteredRows.map((row, index) => (
              <Box
                key={row.key}
                style={{
                  display: 'grid',
                  gridTemplateColumns:
                    'minmax(150px, 170px) minmax(90px, 110px) minmax(150px, 220px) minmax(420px, 1fr)',
                  gap: 12,
                  alignItems: 'start',
                  padding: '10px 12px',
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  background:
                    index % 2 === 0
                      ? theme.palette.action.hover
                      : 'transparent',
                }}
              >
                <Typography
                  variant="body2"
                  style={{
                    fontFamily: 'Menlo, Monaco, Consolas, monospace',
                    fontSize: 12,
                  }}
                >
                  {row.timestamp}
                </Typography>
                <Box>
                  <StatusChip value={row.severity} />
                </Box>
                <Typography
                  variant="body2"
                  style={{
                    fontFamily: 'Menlo, Monaco, Consolas, monospace',
                    fontSize: 12,
                  }}
                >
                  {row.source}
                </Typography>
                <Typography variant="body2" style={messageCellStyle}>
                  {row.message}
                </Typography>
              </Box>
            ))
          )}
        </Box>
      </Box>
    </Box>
  );
};
